/*
  Every set of one game: the game page's Top 10, table and A-Z index, and the set page's
  header (its rank in the game needs the whole catalogue: ranking against the first 200
  would put a set past them in the wrong place, or nowhere).

  The API caps a page at 200 rows (D31), so the catalogue is several requests. Page 0 is
  an ordinary subscription; its `pagination.total` says which offsets remain, and those
  are all dispatched AT ONCE rather than one after another (lib/setPages). On Pokémon
  that is the difference between six round trips in a row and two. The game page and the
  set page ask with the same arguments ({ gameId, offset, limit: 200 }), so whichever a
  visitor reaches second is served from RTK's cache.

  `currentData`, not `data`, for page 0: `data` would still hold the previous game's first
  page while this game's is in flight. The later pages are filed under the game they were
  asked for, so a payload from a game the visitor has left can never land in this one's
  list. A failed page stops the hook reporting `complete` and surfaces as `error`; nothing
  retries in a loop.
*/

import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { assemblePages, remainingPageOffsets, SETS_PAGE_SIZE } from 'lib/setPages';
import { pcgPublicV2Api, useGetSetsQuery } from 'services/pcgPublicV2';
import type { AppDispatch } from 'store';
import type { GameSetSummaryDto } from 'types/v2/catalog';

export interface AllGameSets {
  /* The pages in hand, in page order. The whole catalogue only once `complete`. */
  sets: GameSetSummaryDto[];
  complete: boolean;
  /* Rows in the catalogue, archived included, from page 0. Undefined until it lands. */
  total: number | undefined;
  /* Page 0 on its own: the most graded sets, since the API sorts by cardsGraded. */
  firstPage: GameSetSummaryDto[] | undefined;
  error: unknown;
}

/* Per game: the pages that landed and the pages that failed, both keyed by offset. */
type LaterPages = Record<
  string,
  { pages: Record<number, GameSetSummaryDto[]>; errors: Record<number, unknown> }
>;

export const useAllGameSets = (gameId: number | undefined): AllGameSets => {
  const dispatch = useDispatch<AppDispatch>();
  const [later, setLater] = useState<LaterPages>({});

  const { currentData: first, error: firstError } = useGetSetsQuery(
    { gameId, offset: 0, limit: SETS_PAGE_SIZE },
    { skip: gameId === undefined }
  );
  /* The rows carry their gameId, so a cached page 0 of another game is not taken for this one's. */
  const firstIsThisGame = !!first && (first.data.length === 0 || first.data[0].gameId === gameId);
  const firstPage = firstIsThisGame ? first?.data : undefined;
  const total = firstIsThisGame ? first?.pagination?.total ?? first?.data.length : undefined;

  useEffect(() => {
    if (gameId === undefined || total === undefined) {
      return undefined;
    }
    const key = String(gameId);
    let cancelled = false;
    const requests = remainingPageOffsets(total).map(offset => {
      const request = dispatch(
        pcgPublicV2Api.endpoints.getSets.initiate({ gameId, offset, limit: SETS_PAGE_SIZE })
      );
      request.unwrap().then(
        result => {
          if (cancelled) return;
          /*
            A page that lands clears its OWN earlier failure: coming back to a game re-asks
            for a page that failed before, and its success must not leave that error behind.
            Another page's failure stays until that page lands.
          */
          setLater(previous => {
            const { [offset]: _cleared, ...errors } = previous[key]?.errors ?? {};
            return {
              ...previous,
              [key]: { pages: { ...previous[key]?.pages, [offset]: result.data }, errors },
            };
          });
        },
        error => {
          if (cancelled) return;
          setLater(previous => ({
            ...previous,
            [key]: {
              pages: previous[key]?.pages ?? {},
              errors: { ...previous[key]?.errors, [offset]: error },
            },
          }));
        }
      );
      return request;
    });
    return () => {
      cancelled = true;
      requests.forEach(request => request.unsubscribe());
    };
  }, [dispatch, gameId, total]);

  const thisGame = gameId === undefined ? undefined : later[String(gameId)];

  return useMemo(() => {
    const pages: Record<number, GameSetSummaryDto[]> = { ...thisGame?.pages };
    if (firstPage) {
      pages[0] = firstPage;
    }
    const { rows, complete } = assemblePages(pages, total);
    return {
      sets: rows,
      complete,
      total,
      firstPage,
      error: firstError ?? Object.values(thisGame?.errors ?? {})[0],
    };
  }, [thisGame, firstPage, total, firstError]);
};
